import classNames from "classnames";
import { FC, useContext, useEffect, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import { useCostCentersApi } from "../../http/userCostCenters/api";
import { UserCostCenter, UserCostCenterImportError, UserCostCentersWithErrors } from "../../http/userCostCenters/types";
import { formatDate, formatTimeStamp } from "../../types/utils";
import Button from "../ui/Button";
import DatePicker from "../../ui/DatePicker";
import { getHistoryAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SubmitBar from "../ui/SubmitBar";
import Table from "../ui/Table";
import styles from "./UserCostCentersDialog.module.css";

interface UserCostCentersDialogProps {
  userCostCentersWithErrors: UserCostCentersWithErrors & { userId: string };
  onClose: (reload: boolean) => void;
  onSave: () => void;
}

type EditUserCostCenterRow = UserCostCenter & { added?: boolean; removed?: boolean; error?: boolean };

type Errors = {
  rows?: Map<number, { from?: boolean; inverted?: boolean; costCenter?: boolean }>;
  overlap: boolean;
};

const UserCostCentersDialog: FC<UserCostCentersDialogProps> = ({
  onClose,
  onSave,
  userCostCentersWithErrors,
}: UserCostCentersDialogProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const costCenterApi = useCostCentersApi();
  const userSettings = useUserSettings();

  const [costCenters, setCostCenters] = useState<EditUserCostCenterRow[]>(userCostCentersWithErrors.costCenters);

  useEffect(() => {
    setCostCenters(userCostCentersWithErrors.costCenters);
  }, [userCostCentersWithErrors.costCenters]);

  const [errors, setErrors] = useState<Errors>({ overlap: false });

  const valid = (costCenters: EditUserCostCenterRow[]) => {
    const ers = new Map<number, { from?: boolean; inverted?: boolean; costCenter?: boolean }>();
    for (let i = 0; i < costCenters.length; i++) {
      const cc = costCenters[i];

      const errorFields: { from?: boolean; inverted?: boolean; costCenter?: boolean } = {};

      if (!cc?.from) {
        errorFields.from = true;
      }
      if (cc?.from && cc?.to && cc.from >= cc.to) {
        errorFields.inverted = true;
      }
      if (!cc?.costCenter) {
        errorFields.costCenter = true;
      }

      if (Object.keys(errorFields).length > 0) {
        ers.set(i, errorFields);
      }
    }

    // Check for overlaps
    const overlap = doOverlap(costCenters);
    setErrors({ rows: ers, overlap });

    return !ers.size && !overlap;
  };

  const upsertCostCenters = async () => {
    const values = costCenters.filter((x) => !x.removed);
    if (valid(values)) {
      const body = values.map((x) => ({ ...x, to: x.to?.trim() ? x.to : undefined }));
      try {
        await costCenterApi.upsertAdminUserCostCenters(userCostCentersWithErrors.userId, body);
        dispatch(showSuccessNotification(i18n.t));
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
      onSave();
    }
  };

  const hasErrors = !!userCostCentersWithErrors?.importErrors && userCostCentersWithErrors?.importErrors.length > 0;

  return (
    <ModalDialog onClose={() => onClose(false)} title={i18n.t.common.userCostCenters}>
      {hasErrors ? (
        <>
          <div className={styles["import-error-title"]}>{i18n.t.common.importErrors}</div>
          <div
            className={styles["import-error-text"]}
            dangerouslySetInnerHTML={{ __html: i18n.t.common.importErrorsText }}
          ></div>
          <Table<UserCostCenterImportError>
            head={
              <>
                <th align="left">{i18n.t.common.when}</th>
                <th align="left">{i18n.t.common.from}</th>
                <th align="left">{i18n.t.common.to}</th>
                <th align="left">{i18n.t.common.costCenter}</th>
                <th></th>
              </>
            }
            renderRow={(item) => (
              <>
                <td>{formatTimeStamp(item.createdAt, i18n.language)}</td>
                <td>{formatDate(item.from, i18n.language)}</td>
                <td>{!!item.to && formatDate(item.to, i18n.language)}</td>
                <td>{item.costCenter}</td>
              </>
            )}
            items={userCostCentersWithErrors?.importErrors}
            rowActions={(item) => [
              getHistoryAction(i18n, () =>
                setCostCenters([
                  ...costCenters,
                  {
                    from: item.from,
                    to: item.to,
                    costCenter: item.costCenter,
                  },
                ]),
              ),
            ]}
          />
          <div className={styles["sub-title"]}>{i18n.t.common.history}</div>
        </>
      ) : undefined}
      <Table<EditUserCostCenterRow>
        head={
          <>
            <th align="left">{i18n.t.common.from}</th>
            <th align="left">{i18n.t.common.to}</th>
            <th align="left">{i18n.t.common.costCenter}</th>
            {userSettings.permissions.userCostCenters.update && <th></th>}
          </>
        }
        rowActions={(item) =>
          userSettings.permissions.userCostCenters.update
            ? [
                {
                  title: item.removed ? i18n.t.common.keep : i18n.t.common.remove,
                  onClick: () =>
                    item.removed
                      ? setCostCenters((prev) => prev.map((x) => ({ ...x, removed: false })))
                      : item.added
                        ? setCostCenters((prev) => prev.filter((x) => x !== item))
                        : setCostCenters((prev) => prev.map((x) => ({ ...x, removed: x === item }))),
                },
              ]
            : []
        }
        hoverTr={false}
        renderRow={(item, index) => (
          <>
            <td
              className={classNames(styles.withAdd, {
                [styles.removed]: item.removed,
                [styles.inline]: !item.removed,
                [styles.error]: !!errors.rows?.get(index)?.from || !!errors.rows?.get(index)?.inverted,
              })}
            >
              <span className={item.added ? styles.new : styles.invisible}>* </span>
              {item.removed ? (
                <span>{!!item.from && formatDate(item.from, i18n.language)}</span>
              ) : (
                <div className={styles.dateInputClearable}>
                  <DatePicker
                    disabled={!userSettings.permissions.userCostCenters.update}
                    noBorder
                    value={item.from}
                    onChange={(s) =>
                      setCostCenters((prev) =>
                        prev.map((x) => ({
                          ...x,
                          from: item === x && s != null ? s : x.from,
                        })),
                      )
                    }
                  />
                </div>
              )}
            </td>
            <td
              className={classNames({
                [styles.removed]: item.removed,
                [styles.inline]: !item.removed,
                [styles.error]: !!errors.rows?.get(index)?.inverted,
              })}
            >
              {item.removed ? (
                <span>{!!item.to && formatDate(item.to, i18n.language)}</span>
              ) : (
                <div className={styles.dateInputClearable}>
                  <DatePicker
                    disabled={!userSettings.permissions.userCostCenters.update}
                    noBorder
                    value={item.to ?? ""}
                    clearable
                    onChange={(s) =>
                      setCostCenters((prev) =>
                        prev.map((x) => ({
                          ...x,
                          to: item === x ? (s ?? undefined) : x.to,
                        })),
                      )
                    }
                  />
                </div>
              )}
            </td>
            <td
              className={classNames({
                [styles.removed]: item.removed,
                [styles.inline]: !item.removed,
                [styles.error]: !!errors.rows?.get(index)?.costCenter,
              })}
            >
              {item.removed ? (
                <span>{item.costCenter}</span>
              ) : (
                <input
                  disabled={!userSettings.permissions.userCostCenters.update}
                  value={item.costCenter}
                  onChange={(e) =>
                    setCostCenters((prev) =>
                      prev.map((x) => ({
                        ...x,
                        costCenter: item === x ? e.target.value : x.costCenter,
                      })),
                    )
                  }
                />
              )}
            </td>
          </>
        )}
        items={costCenters}
      />
      <div className={classNames(styles.error, { [styles.invisible]: !errors.overlap })}>
        {i18n.t.common.someCostCenterPeriodsOverlap}
      </div>
      <SubmitBar>
        {userSettings.permissions.userCostCenters.update && (
          <Button
            buttonProps={{
              onClick: () => setCostCenters((prev) => [...prev, { from: "", costCenter: "", added: true }]),
            }}
            type="secondary"
          >
            {i18n.t.common.add}
          </Button>
        )}
        {userSettings.permissions.userCostCenters.update && (
          <Button
            buttonProps={{
              onClick: upsertCostCenters,
            }}
          >
            {i18n.t.common.save}
          </Button>
        )}
      </SubmitBar>
    </ModalDialog>
  );
};

export default UserCostCentersDialog;

export function doOverlap(periods: { from?: string | null; to?: string | null }[]): boolean {
  const validPeriods = periods
    .map((p) => ({
      from: p.from?.trim() || "", // fallback to empty string
      to: p.to?.trim() || "9999-12-31", // fallback to far future
    }))
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));

  for (let i = 1; i < validPeriods.length; i++) {
    const prev = validPeriods[i - 1];
    const curr = validPeriods[i];

    // Skip if current or previous `from` is missing
    if (!curr?.from || !prev?.to) continue;

    if (curr.from < prev.to) {
      return true;
    }
  }

  return false;
}
